// Cookie utilities for theme preference and cache management
import { IClientInfo } from './types';

const THEME_COOKIE_NAME = 'theme-preference';
const THEME_CACHE_COOKIE_NAME = 'theme-cache';
const THEME_COOKIE_MAX_AGE = 3600; // 1 hour in seconds (reduced from 24h)

export interface ThemeCookieValue {
  themeName: string;
  timestamp: number;
}

export interface ThemeCacheCookieValue {
  themeName: string;
  themeData: IClientInfo;
  timestamp: number;
  switchAllowed: boolean;
  availableThemes: string[];
}

/**
 * Set theme preference cookie with 1-hour expiration
 */
export function setThemePreferenceCookie(themeName: string): void {
  if (typeof window === 'undefined') {
    return; // SSR guard
  }

  const cookieValue: ThemeCookieValue = {
    themeName,
    timestamp: Date.now(),
  };

  // Detect HTTPS at runtime for secure cookie transmission
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const cookieAttrs = [
    `${THEME_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(cookieValue))}`,
    `Max-Age=${THEME_COOKIE_MAX_AGE}`,
    'Path=/',
    'SameSite=Lax',
  ];

  // Always add Secure flag when using HTTPS
  if (isSecure) {
    cookieAttrs.push('Secure');
  }

  document.cookie = cookieAttrs.join('; ');
}

/**
 * Set theme cache cookie with full theme data for SSR
 */
export function setThemeCacheCookie(
  themeName: string,
  themeData: IClientInfo,
  switchAllowed: boolean,
  availableThemes: string[]
): void {
  if (typeof window === 'undefined') {
    return; // SSR guard
  }

  const cookieValue: ThemeCacheCookieValue = {
    themeName,
    themeData,
    timestamp: Date.now(),
    switchAllowed,
    availableThemes,
  };

  try {
    // Detect HTTPS at runtime for secure cookie transmission
    const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
    const cookieAttrs = [
      `${THEME_CACHE_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(cookieValue))}`,
      `Max-Age=${THEME_COOKIE_MAX_AGE}`,
      'Path=/',
      'SameSite=Lax',
    ];

    // Always add Secure flag when using HTTPS
    if (isSecure) {
      cookieAttrs.push('Secure');
    }

    document.cookie = cookieAttrs.join('; ');
  } catch (error) {
    console.warn('Failed to set theme cache cookie (data too large):', error);
    // If cookie is too large, just set preference
    setThemePreferenceCookie(themeName);
  }
}

/**
 * Get theme preference from cookie (client-side)
 */
export function getThemePreferenceCookie(): string | null {
  if (typeof window === 'undefined') {
    return null; // SSR guard
  }

  const cookies = document.cookie.split(';');
  
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === THEME_COOKIE_NAME && value) {
      try {
        const parsed: ThemeCookieValue = JSON.parse(decodeURIComponent(value));
        
        // Check if cookie is still valid (within 1 hour)
        const now = Date.now();
        const cookieAge = now - parsed.timestamp;
        const isValid = cookieAge < (THEME_COOKIE_MAX_AGE * 1000);
        
        if (isValid) {
          return parsed.themeName;
        } else {
          // Cookie expired, remove it
          clearThemePreferenceCookie();
          return null;
        }
      } catch (error) {
        console.warn('Failed to parse theme preference cookie:', error);
        clearThemePreferenceCookie();
        return null;
      }
    }
  }
  
  return null;
}

/**
 * Get theme cache from cookie (client-side)
 */
export function getThemeCacheCookie(themeName?: string): ThemeCacheCookieValue | null {
  if (typeof window === 'undefined') {
    return null; // SSR guard
  }

  const cookies = document.cookie.split(';');
  
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === THEME_CACHE_COOKIE_NAME && value) {
      try {
        const parsed: ThemeCacheCookieValue = JSON.parse(decodeURIComponent(value));
        
        // Check if cookie is still valid (within 1 hour)
        const now = Date.now();
        const cookieAge = now - parsed.timestamp;
        const isValid = cookieAge < (THEME_COOKIE_MAX_AGE * 1000);
        
        // Check if this is the theme we're looking for
        const requestedTheme = themeName || 'default';
        const isCorrectTheme = parsed.themeName === requestedTheme;
        
        if (isValid && isCorrectTheme) {
          return parsed;
        } else if (!isValid) {
          // Cookie expired, remove it
          clearThemeCacheCookie();
          return null;
        }
      } catch (error) {
        console.warn('Failed to parse theme cache cookie:', error);
        clearThemeCacheCookie();
        return null;
      }
    }
  }
  
  return null;
}

/**
 * Get theme preference from cookie string (server-side)
 */
export function getThemePreferenceFromCookieString(cookieString: string): string | null {
  if (!cookieString) {
    return null;
  }

  const cookies = cookieString.split(';');
  
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === THEME_COOKIE_NAME && value) {
      try {
        const parsed: ThemeCookieValue = JSON.parse(decodeURIComponent(value));
        
        // Check if cookie is still valid (within 1 hour)
        const now = Date.now();
        const cookieAge = now - parsed.timestamp;
        const isValid = cookieAge < (THEME_COOKIE_MAX_AGE * 1000);
        
        if (isValid) {
          return parsed.themeName;
        }
      } catch (error) {
        console.warn('Failed to parse theme preference cookie:', error);
        return null;
      }
    }
  }
  
  return null;
}

/**
 * Get theme cache from cookie string (server-side)
 */
export function getThemeCacheFromCookieString(cookieString: string, themeName?: string): ThemeCacheCookieValue | null {
  if (!cookieString) {
    return null;
  }

  const cookies = cookieString.split(';');
  
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === THEME_CACHE_COOKIE_NAME && value) {
      try {
        const parsed: ThemeCacheCookieValue = JSON.parse(decodeURIComponent(value));
        
        // Check if cookie is still valid (within 1 hour)
        const now = Date.now();
        const cookieAge = now - parsed.timestamp;
        const isValid = cookieAge < (THEME_COOKIE_MAX_AGE * 1000);
        
        // Check if this is the theme we're looking for
        const requestedTheme = themeName || 'default';
        const isCorrectTheme = parsed.themeName === requestedTheme;
        
        if (isValid && isCorrectTheme) {
          return parsed;
        }
      } catch (error) {
        console.warn('Failed to parse theme cache cookie:', error);
        return null;
      }
    }
  }
  
  return null;
}

/**
 * Clear theme preference cookie
 */
export function clearThemePreferenceCookie(): void {
  if (typeof window === 'undefined') {
    return; // SSR guard
  }

  // Detect HTTPS at runtime for secure cookie transmission
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const secureFlag = isSecure ? ' Secure;' : '';
  document.cookie = `${THEME_COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax;${secureFlag}`;
}

/**
 * Clear theme cache cookie
 */
export function clearThemeCacheCookie(): void {
  if (typeof window === 'undefined') {
    return; // SSR guard
  }

  // Detect HTTPS at runtime for secure cookie transmission
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const secureFlag = isSecure ? ' Secure;' : '';
  document.cookie = `${THEME_CACHE_COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax;${secureFlag}`;
}

/**
 * Check if theme preference cookie exists and is valid
 */
export function hasValidThemePreferenceCookie(): boolean {
  return getThemePreferenceCookie() !== null;
}

/**
 * Check if theme cache cookie exists and is valid
 */
export function hasValidThemeCacheCookie(themeName?: string): boolean {
  return getThemeCacheCookie(themeName) !== null;
}